import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Tag, Button } from "antd";
import { useSearchParams, useNavigate } from "react-router-dom";
import theme from "../theme";
import api from "../services/api";
import { toast } from "react-toastify";
import { useCart } from "../contexts/CartContext";
import AssociatedTests from "../components/AssociatedTests";
import img_placeholder from "../images/test-placeholder.png";
import BackButton from "../components/BackButton";
import { Expert } from "../types/industry"
import "bootstrap-icons/font/bootstrap-icons.css";
import { ExportOutlined } from '@ant-design/icons';

interface TestData {
  id: number;
  name: string;
  description?: string;
  price: number;
  industry_name: string;
  industry_color: string;
  question_count: number;
  time_limit: number;
  passing_grade: number;
  first_question?: {
    id: number;
    prompt: string;
    image?: string;
    answers: Array<{
      id: number;
      text: string;
    }>;
  };
  expert: Expert
}

const TestDetails: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [testData, setTestData] = useState<TestData | null>(null);
  const [loading, setLoading] = useState(true);
  const { addItem, isInCart } = useCart();

  const loadTestDetails = useCallback(async () => {
    try {
      setLoading(true);
      const testId = searchParams.get('test_id');
      if (!testId) {
        toast.error('Assessment ID is required');
        navigate('/browse-tests');
        return;
      }

      const response = await api.get(`test-details/?test_id=${testId}`);
      setTestData(response.data);
    } catch (error: any) {
      console.error('Failed to load assessment details:', error);
      toast.error('Failed to load assessment details. Please try again.');
      navigate('/browse-tests');
    } finally {
      setLoading(false);
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    loadTestDetails();
  }, [loadTestDetails]);

  const handleAddToCart = () => {
    if (testData && !isInCart(testData.id)) {
      addItem({
        id: testData.id,
        name: testData.name,
        price: testData.price,
        industry_name: testData.industry_name,
        industry_color: testData.industry_color,
      });
      toast.success(`${testData.name} added to cart!`);
    }
  };

  const formatTime = (time: string) => {
    let limit = "";

    if (time.trim() === "") {
      limit = "No"
    } else {
      limit = time + ":00"
    }

    limit += " time limit"
    return limit;
  }

  if (loading) {
    return (
      <div style={bg}>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          Loading assessment details...
        </div>
      </div>
    );
  }

  if (!testData) {
    return (
      <div style={bg}>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          Assessment not found.
        </div>
      </div>
    );
  }

  return (
	<>
	  <BackButton />
		<div style={bg}>
			{/* Main card */}
			<div style={card}>
				<div style={cardHeader}>
				<h1 style={heading}>{testData.name}</h1>

				{/* Tags */}
				<Tag style={tagStyle}>
					{testData.industry_name}
				</Tag>
				</div>

				<div style={metaContainer}>
				<span style={metaText}>{testData.question_count} {testData.question_count === 1 ? 'question' : 'questions'}</span>
				<span style={metaText}>{formatTime(String(testData.time_limit))}</span>
				</div>

				<Row gutter={60}>
				<Col span={12} style={colStyle}>
					<p>
					{testData.description || 'No description available for this assessment.'}
					</p>

					<div style={buyNow}>

						{testData.expert && (
							<div style={expertSection}>
								<div style={expertTitle}>This test has been validated by an expert!</div>
								<div style={expertInnerWrapper}>
									<div>
										<img style={expertPhoto} src={testData.expert?.photo} />
									</div>
									<div>
										<div style={expertName}>{testData.expert?.name}</div>
										<div style={expertAbout}>{testData.expert?.about}</div>
										<div>
											<a style={expertLinkedIn} href={testData.expert?.linkedin}>View on linkedin
												<ExportOutlined style={{ marginLeft: 5, fontSize: 11, fontWeight: "bold" }} />
											</a>
										</div>
									</div>
								</div>
							</div>
						)}

						<hr />
						<Row
							style={{
							justifyContent: "space-between",
							alignItems: "center",
							}}
						>
							<div className="test-price" style={price}>{`$${testData.price}`}</div>
							<Button
							type="primary"
							style={addBtn}
							onClick={handleAddToCart}
							disabled={isInCart(testData.id)}
							>
							{isInCart(testData.id) ? 'Already in Cart' : 'Buy now'}
							</Button>
						</Row>
					</div>
				</Col>

				<Col span={12}>
					<h2 style={explanationTitle}>What you'll receive:</h2>
					<img style={previewImg} src={img_placeholder} alt="Preview of assessment results report" />
					<div style={explanation}>
					Once you’ve completed this assessment, you’ll get a full breakdown of your results that showcases your skills. You can download this as a PDF to use in your own CV, or even share your results with potential employers directly in TestInvest!
					</div>
				</Col>
				</Row>
			</div>
			<AssociatedTests id={testData.id} />
		</div>
	</>
  );
};

/* ─── styles ─── */

const bg: React.CSSProperties = {
  minHeight: "100vh",
  padding: "20px 20%",
};

const cardHeader: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  marginBottom: "10px"
};

const heading: React.CSSProperties = {
  fontSize: 20,
  fontWeight: 700,
  color: theme.colors.primary,
  margin: 0,
};

const card: React.CSSProperties = {
  background: "#fff",
  borderRadius: 18,
  padding: 32,
  position: "relative",
};

const tagStyle: React.CSSProperties = {
  fontSize: 10,
  borderRadius: 9999,
  marginBottom: "10px",
  color: "#8E99BA",
  background: "#F4F8FF",
  padding: "0px 20px",
  height: "18px",
  lineHeight: "1rem",
  border: "none"
};

const price: React.CSSProperties = {
  fontSize: 40,
  fontWeight: 700,
  color: "#0051FF",
};

const addBtn: React.CSSProperties = {
  borderRadius: 9999,
  color: "white",
  background: "#1D1F48",
  border: "none",
  outline: "none",
  height: 38,
  width: 156,
  fontWeight: "bold",
  fontSize: 14,
};

const backBtn: React.CSSProperties = {
  position: "relative",
  top: "1.5px",
  height: "11px"
}

const metaContainer: React.CSSProperties = {
  display: "flex",
  gap: "10px",
  marginBottom: "16px",
  height: 23
};

const metaText: React.CSSProperties = {
  fontSize: 12,
  color: "#666",
  fontWeight: 500,
  border: "1px solid #E6EBFA",
  padding: "0px 15px",
  borderRadius: "5px"
};

const explanationTitle: React.CSSProperties = {
  color: "#0051FF",
  fontWeight: "bold",
  fontSize: "20px"
}

const explanation: React.CSSProperties = {
  color: "#0051FF",
  border: "solid 1px #0051FF",
  background: "#F3F7FF",
  padding: "20px",
  borderRadius: "4px",
  textAlign: "center",
  fontSize: "14px"
}

const colStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "space-between",
  alignContent: "space-between",
  flexWrap: "wrap"
}

const previewImg: React.CSSProperties = {
  width: "100%",
}

const buyNow: React.CSSProperties = {
  width: "100%",
}

const expertSection: React.CSSProperties = {
  border: "solid 1px #2EEDB0",
  borderRadius: 4,
  background: "#F0FFFA",
  padding: "8px 24px 8px 24px"
}

const expertTitle: React.CSSProperties = {
	fontWeight: "bold",
	marginBottom: 10
}

const expertName: React.CSSProperties = {
	fontWeight: "bold",
	fontSize: 16
}

const expertAbout: React.CSSProperties = {
	color: "#616165"
}

const expertPhoto: React.CSSProperties = {
	borderRadius: "50%",
	height: 48,
	width: 48,
}

const expertInnerWrapper: React.CSSProperties = {
	display: "flex",
	gap: "10px"
}

const expertLinkedIn: React.CSSProperties = {
	color: "#00CFAD",
	textDecoration: "none"
}

export default TestDetails;
